import { List, Set } from "immutable"
import * as R from "ramda"
import { differenceInDays } from "date-fns"

import { getUTCDate } from "common/services/momentHelpers/momentHelpers"
import { get, getOr } from "common/utils/get"
import has from "common/utils/has"
import isUndefined from "common/utils/isUndefined"
import isEmpty from "common/utils/isEmpty"
import cuckoo from "../cuckoo/cuckoo"
import Trip from "../../records/Trip"
import JourneyProvider from "../../records/JourneyProvider"
import Journey from "../../records/Journey"
import mapperFlight from "./mapperFlight"
import mapperTransfer from "./mapperTransfer"
import { cuckooEvents } from "../../consts/enums"
import {
  PROVIDER_EXCLUDE_BOOKING,
  PROVIDER_KIWI_NAME,
  PROVIDER_KIWI_CODE,
} from "common/consts/providerConsts"
import type Place from "../../records/Place"
import type { Splitster } from "common/types/splitster"

// TODO: @boris: Consider using some of new code from https://github.com/kiwi-code/frontend/commit/bce02c741feacd210d830c78a7b817788337cff4

import type { BookingInput } from "../../../mmb/records/bookingInput"
import {
  filterHiddenFlightOut,
  formatForHiddenFlightsMapping,
  hasHiddenFlight,
  hiddenFlightFormattingSupported,
} from "./journeyHiddenFlightTools"

const APIS = {
  CHECK_FLIGHTS_API: "check_flights",
  FLIGHTS_API: "flights",
  BOOKING_API: "bookings",
  TRANSFERS_API: "transfers",
}

function determineApiFormat(flightObj) {
  if (has("src.airport", flightObj) && has("dst.airport", flightObj)) {
    return APIS.TRANSFERS_API
  }
  if (flightObj && has("src", flightObj) && has("dst", flightObj)) {
    return APIS.CHECK_FLIGHTS_API
  }
  if (has("departure.where.code", flightObj) && has("arrival.where.code", flightObj)) {
    return APIS.BOOKING_API
  }
  if (flightObj && has("flyFrom", flightObj) && has("flyTo", flightObj)) {
    return APIS.FLIGHTS_API
  }
  return null
}

function extractArrivalCode(flightObj) {
  switch (determineApiFormat(flightObj)) {
    case APIS.TRANSFERS_API:
      return flightObj.dst.airport
    case APIS.CHECK_FLIGHTS_API:
      return flightObj.dst
    case APIS.BOOKING_API:
      return flightObj.arrival.where.code
    case APIS.FLIGHTS_API:
      return flightObj.flyTo
    default:
      return ""
  }
}

function extractDepartureCode(flightObj) {
  switch (determineApiFormat(flightObj)) {
    case APIS.TRANSFERS_API:
      return flightObj.src.airport
    case APIS.CHECK_FLIGHTS_API:
      return flightObj.src
    case APIS.BOOKING_API:
      return flightObj.departure.where.code
    case APIS.FLIGHTS_API:
      return flightObj.flyFrom
    default:
      return ""
  }
}

// eslint-disable-next-line flowtype/no-weak-types
export function fillPlacesToFlightObjMutable(flightObj: Object, places: Map<string, Place>) {
  // eslint-disable-line flowtype/no-weak-types
  if (places.get(flightObj.src) && places.get(flightObj.src).get("city")) {
    flightObj.src_name = places.get(flightObj.src).get("city").name // eslint-disable-line no-param-reassign
    flightObj.src_city = places.get(flightObj.src).get("city").id // eslint-disable-line no-param-reassign
  }
  if (places.get(flightObj.dst) && places.get(flightObj.dst).get("city")) {
    flightObj.dst_name = places.get(flightObj.dst).get("city").name // eslint-disable-line no-param-reassign
    flightObj.dst_city = places.get(flightObj.dst).get("city").id // eslint-disable-line no-param-reassign
  }
  flightObj.src_station = places.get(flightObj.src).get("station") || flightObj.src_station // eslint-disable-line no-param-reassign
  flightObj.dst_station = places.get(flightObj.dst).get("station") || flightObj.dst_station // eslint-disable-line no-param-reassign
}

export default function(
  rawInput: BookingInput,
  requestSource?: string,
  disableHiddenFlightsFiltering?: boolean = false,
  airlines,
  splitster: Splitster,
) {
  const shouldMapHiddenFlights =
    disableHiddenFlightsFiltering &&
    hasHiddenFlight(rawInput) &&
    hiddenFlightFormattingSupported(rawInput)

  let hasHoldBagsAllowed

  const input = shouldMapHiddenFlights
    ? formatForHiddenFlightsMapping(rawInput) // format for hidden flights
    : filterHiddenFlightOut(rawInput) // filter out hidden flights by default

  if (!R.isNil(input.cabin_baggage_only)) {
    hasHoldBagsAllowed = !input.cabin_baggage_only
  } else if (get(["route", 0, "route"], input)) {
    // multicity and salesman
    if (input.bags_price) {
      // is salesman
      hasHoldBagsAllowed = !isEmpty(input.bags_price)
    } else {
      // is multicity
      hasHoldBagsAllowed = input.route.every(x => !isEmpty(x.bags_price))
    }
    // mmb
  } else if (get(["baggages", "notices", "hold_bags_disabled_reason"], input)) {
    hasHoldBagsAllowed = false
  } else if (!R.isNil(input.bags_price)) {
    hasHoldBagsAllowed = !isEmpty(input.bags_price)
  } else {
    hasHoldBagsAllowed = true
  }

  // input.searchProvider is set from Umbrella, when using flights API & meta search, source is same for whole request
  const source = input.searchProvider || requestSource

  let journey = new Journey({
    bookingToken: input.booking_token,
    hasHoldBagsAllowed,
    debugColor: input.debugColor,
    debugString: input.debugString,
    id: input.id,
    _original: input,
    sources: Set([source]),
    quality: input.quality,
    disabledBagsInMmb: getOr(false, "disabled_bags_in_mmb", input),
    hiddenFlightsType: getOr(null, "hiddenFlightsType", input),
    countryFrom: getOr("", ["countryFrom", "code"], input),
    countryTo: getOr("", ["countryTo", "code"], input),
  })

  if (R.is(Number, input.price)) {
    journey = journey.setIn(["prices", "default"], input.price)
  } else if (!isEmpty(input.price)) {
    journey = journey
      .setIn(["prices", "default"], input.price.paid_by_pap)
      .setIn(["prices", "paidByKiwi"], input.price.paid_by_kiwi)
      .setIn(["prices", "overnight"], input.price.overnight) // price of overnight included in SHOT alternative
  }

  if (!isUndefined(input.new_overnight)) {
    journey = journey.set("newOvernight", input.new_overnight)
  }

  // Search uses this mapper, they don't have flights
  if (input.flights) {
    const prioFlights = input.flights.filter(
      flightObj => flightObj.forced_priority_boarding && flightObj.airline.code === "FR",
    )

    const prioBoardingNeededForCabinBag = prioFlights.length > 0

    const daysUntilFirstDeparture = prioBoardingNeededForCabinBag
      ? differenceInDays(getUTCDate(prioFlights[0].dtime * 1000), getUTCDate())
      : undefined

    if (
      splitster &&
      hasHoldBagsAllowed &&
      prioBoardingNeededForCabinBag &&
      daysUntilFirstDeparture < 10
    ) {
      cuckoo.track(cuckooEvents.BOOKING_PB_SOLD_OUT_MARKED_FLIGHTS)
      if (splitster.get("PB_SOLD_OUT").value === "on") {
        input.flights.forEach(flightObj => {
          // eslint-disable-next-line
          flightObj.forced_priority_boarding = false

          // If prio boarding ancillary is defined for this flight, turn it off
          if (
            R.pathOr(
              false,
              ["additional_services", "priority_boarding", "data", flightObj.id, "is_possible"],
              rawInput,
            )
          ) {
            // eslint-disable-next-line
            rawInput.additional_services.priority_boarding.data[flightObj.id].is_possible = false
          }
        })
      }
    }
  }

  let isMulticity
  let tripObjs = []
  if (typeof input.segments === "object") {
    isMulticity = !!(input.segments && input.segments.length && !input.flights.some(get("return")))

    if (isMulticity) {
      input.flights.forEach((flightObj, flightIndex) => {
        let tripIndex = 0
        while (input.segments[tripIndex] && flightIndex >= parseInt(input.segments[tripIndex])) {
          tripIndex++
        }
        if (!tripObjs[tripIndex]) tripObjs[tripIndex] = []
        tripObjs[tripIndex].push(flightObj)
      })
    } else {
      input.flights.forEach(flightObj => {
        const tripIndex = flightObj.return ? 1 : 0
        if (!tripObjs[tripIndex]) tripObjs[tripIndex] = []
        tripObjs[tripIndex].push(flightObj)
      })
    }
  } else if (Array.isArray(input.route)) {
    // TODO: Check
    /*
     If Multicity, structure is like:
      { ...
        route: [
          { ..., route: [{flightObj}, {flightObj}, ...] }
        ]
      }

      if not Multicity, structure is like
      { ...
        route: [
          { flightObj: ..., return: 0 }
        ]
      }
    */
    isMulticity = input.route.some(x => x.route !== undefined)
    if (isMulticity) {
      tripObjs = input.route.map(t => t.route)
    } else {
      tripObjs = []
      input.route.forEach(flightObj => {
        const tripIndex = flightObj.return ? 1 : 0
        if (!tripObjs[tripIndex]) tripObjs[tripIndex] = []
        tripObjs[tripIndex].push(flightObj)
      })
    }
  } else if (
    // New format: array of array
    Array.isArray(get("trips", input)) &&
    Array.isArray(get(["trips", 0], input))
  ) {
    isMulticity = true // SHOT, alternatives currently don't receive information about segments/multicity journeys, so we display everything as multicity
    tripObjs = input.trips
  } else {
    cuckoo.error(cuckooEvents.INTERNAL_MAPPER_JOURNEY_UNSUPPORTED_FORMAT, { input })
  }

  // we totally do not want to have a journey with no trips...
  // workaround for graphql returning this instead of empty results
  // {"data":{"get_flights_multi":[{"price":null,"deep_link":null,"booking_token":null,"route":[]}]},"invalid":false}
  if (tripObjs.length === 0) return null

  const { transfers } = input
  const containsTransfers = typeof input.transfers === "object"

  tripObjs.forEach((tripObj, tripIndex) => {
    journey = journey.setIn(["trips", tripIndex], new Trip())

    // One API is not returning flights sorted, fix it
    // TODO: Add assertion to API layer, make backend fix it and then remove this
    if (get([0, "dTimeUTC"], tripObj)) tripObj = R.sortBy(get("dTimeUTC"), tripObj) // eslint-disable-line no-param-reassign

    let prevArrivalCode = ""

    tripObj.forEach((flightObj, flightIndex) => {
      // FLIGHTS MAPPER
      journey = journey.updateIn(["trips", tripIndex, "flights"], flights =>
        flights.push(mapperFlight(flightObj, input.passengers, airlines)),
      )

      // PARTS MAPPER (flights with transfers together)
      const currDepartureCode = extractDepartureCode(flightObj)

      if (containsTransfers && prevArrivalCode !== currDepartureCode && !!flightIndex) {
        const transfer = transfers[0]
        if (
          extractDepartureCode(transfer) === prevArrivalCode &&
          extractArrivalCode(transfer) === currDepartureCode
        ) {
          transfers.shift()
          journey = journey.updateIn(["trips", tripIndex, "parts"], flights =>
            flights.push(mapperTransfer(transfer)),
          )
        }
      }

      journey = journey.updateIn(["trips", tripIndex, "parts"], flights =>
        flights.push(mapperFlight(flightObj, input.passengers, airlines)),
      )
      prevArrivalCode = extractArrivalCode(flightObj)
    })
    return tripObj
  })

  if (Array.isArray(input.transfers)) {
    input.transfers.forEach(transferObj => {
      journey = journey.updateIn(["transfers"], tfers => tfers.push(mapperTransfer(transferObj)))
    })
  } else {
    // cuckoo.info("General: Journey mapper: Transfers not in response")
  }

  let type = "return"

  if (isMulticity) {
    type = "multicity"
  } else if (journey.get("trips").size === 1) {
    type = "oneWay"
  }

  journey = journey.set("type", type)
  if (Array.isArray(input.providers) && input.providers.length) {
    const excludedProviders = PROVIDER_EXCLUDE_BOOKING[source] || []

    const providers = List(
      input.providers
        .map(provider => JourneyProvider.fromPlainJS({ ...provider, source }))
        .filter(provider => !excludedProviders.some(excl => provider.code === excl)),
    ).sortBy(provider => provider.price)

    if (providers.isEmpty()) {
      return null
    }

    journey = journey.set("providers", providers)
  } else if (source === "kiwi") {
    journey = journey.set(
      "providers",
      List([
        JourneyProvider.fromPlainJS({
          name: PROVIDER_KIWI_NAME,
          code: PROVIDER_KIWI_CODE,
          price: journey.getPrice(),
          source,
        }),
      ]),
    )
  }

  return journey
}
